import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import apiClient from "../../api/apiClient";
import { ImpersonateModal } from "../modals/ImpersonateModal";

export function ImpersonationHandler() {
  const location = useLocation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [showImpersonateModal, setShowImpersonateModal] = useState(false);
  const [impersonationInfo, setImpersonationInfo] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const impToken = urlParams.get("impersonation_token");
    const tenantId = urlParams.get("tenant_id");

    if (impToken && tenantId) {
      setImpersonationInfo({
        token: decodeURIComponent(impToken),
        tenant_id: tenantId,
        user_id: urlParams.get("user_id"),
        expires_at: urlParams.get("expires_at"),
      });
      setShowImpersonateModal(true);

      // Clean the URL immediately so it's not accidentally lost by redirects
      const url = new URL(window.location.href);
      ["impersonation_token", "tenant_id", "user_id", "expires_at"].forEach(
        (p) => url.searchParams.delete(p),
      );
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, [location.search]);

  const handleImpersonate = async () => {
    setIsImpersonating(true);
    try {
      queryClient.clear();

      const res = await apiClient.get("/me", {
        headers: {
          Authorization: `Bearer ${impersonationInfo.token}`,
          "X-Tenant": impersonationInfo.tenant_id,
        },
      });

      if (res.data?.data) {
        dispatch({ type: "User_data", payload: res.data.data });
        if (res.data.data.permissions) {
          dispatch({
            type: "Userpermission_data",
            payload: res.data.data.permissions,
          });
        }
        dispatch({ type: "Current_Store", payload: null });
        dispatch({ type: "Bearer_token", payload: impersonationInfo.token });
      }
      setShowImpersonateModal(false);
      toast.success("Impersonation successful!");
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Impersonation failed. The token may have expired.";
      toast.error(msg);
      setShowImpersonateModal(false);
    } finally {
      setIsImpersonating(false);
    }
  };

  return (
    <ImpersonateModal
      open={showImpersonateModal}
      onConfirm={handleImpersonate}
      onCancel={() => !isImpersonating && setShowImpersonateModal(false)}
      isLoading={isImpersonating}
    />
  );
}
